import { Text } from '@mantine/core'
import ReactMarkdown from 'react-markdown'

import { CollapsibleContentSection } from '@/components/CollapsibleContentSection'
import { TableContentGrid } from '@/components/TableContentGrid'
import { usePresentationStyles } from '@/hooks/usePresentationStyles'
import { CUSTOM_DELIVERY_MODE, deliveryModeBlurb, deliveryModeLabel, storedDeliveryMode } from '@/utils/deliveryModes'

import { useContentModuleScreenData } from './ContentModuleScreen'

export function CustomizeDeliverySection() {
  const data = useContentModuleScreenData()
  const cm = data.cmQuery.data

  // The stored value is a style `name` — a slug. The catalog is what turns it into the
  // words the picker showed, so this reads it too rather than printing `media-mode` back
  // at someone who chose Media.
  const stylesQuery = usePresentationStyles()

  // Read the same way the editor reads it, or the two disagree about the same module: a
  // module saved before the choice was recorded carries only `is_media_mode`, which the
  // editor resolves to Media and this used to call Custom — and then printed the module's
  // stored prose, which for a media module is generated prompt template, not the expert's.
  // Prose with no choice and no flag is decision 16's real Custom case.
  const stored = storedDeliveryMode(cm ?? {})
  const chosen = stored && stored !== CUSTOM_DELIVERY_MODE ? stored : undefined
  const style = chosen ? stylesQuery.data?.find(s => s.name === chosen) : undefined
  const deliveryMode = chosen
    ? deliveryModeLabel(chosen, style?.displayName || chosen)
    : stored === CUSTOM_DELIVERY_MODE
      ? 'Custom'
      : null

  const rows = [
    {
      heading: 'Delivery Mode',
      content: deliveryMode ?? 'Not specified',
    },
    {
      heading: 'Delivery Style',
      // A chosen mode's stored style is the prompt template the Sidekick is given — the
      // picker deliberately never shows it, so neither does this. The expert's own prose
      // is shown, because in the Custom case it is theirs and they wrote it.
      content: chosen ? (
        <Text>{deliveryModeBlurb(chosen) ?? 'Not specified'}</Text>
      ) : data.cmQuery.data?.presentationStyle ? (
        <ReactMarkdown
          components={{
            p: ({ children }) => <Text mb="xs">{children}</Text>,
            ol: ({ children }) => (
              <Text component="ol" mt={0} mb="xs" style={{ paddingLeft: '1.5rem' }}>
                {children}
              </Text>
            ),
            ul: ({ children }) => (
              <Text component="ul" mt={0} mb="xs" style={{ paddingLeft: '1.5rem' }}>
                {children}
              </Text>
            ),
          }}>
          {data.cmQuery.data.presentationStyle}
        </ReactMarkdown>
      ) : (
        'Not specified'
      ),
    },
    {
      heading: 'Guidance',
      content: data.cmQuery.data?.guidance ? (
        <ReactMarkdown
          components={{
            p: ({ children }) => <Text mb="xs">{children}</Text>,
            ol: ({ children }) => (
              <Text component="ol" mt={0} mb="xs" style={{ paddingLeft: '1.5rem' }}>
                {children}
              </Text>
            ),
            ul: ({ children }) => (
              <Text component="ul" mt={0} mb="xs" style={{ paddingLeft: '1.5rem' }}>
                {children}
              </Text>
            ),
          }}>
          {data.cmQuery.data.guidance}
        </ReactMarkdown>
      ) : (
        'Not specified'
      ),
    },
    {
      heading: 'Personality',
      content: data.cmQuery.data?.presentationPersonality ? (
        <ReactMarkdown
          components={{
            p: ({ children }) => <Text mb="xs">{children}</Text>,
            ol: ({ children }) => (
              <Text component="ol" mt={0} mb="xs" style={{ paddingLeft: '1.5rem' }}>
                {children}
              </Text>
            ),
            ul: ({ children }) => (
              <Text component="ul" mt={0} mb="xs" style={{ paddingLeft: '1.5rem' }}>
                {children}
              </Text>
            ),
          }}>
          {data.cmQuery.data.presentationPersonality}
        </ReactMarkdown>
      ) : (
        'Not specified'
      ),
    },
    /*
    {
      heading: 'Objectives',
      content: data.cmQuery.data?.presentationObjectives ? (
        <ReactMarkdown
          components={{
            p: ({ children }) => <Text mb="xs">{children}</Text>,
            ol: ({ children }) => (
              <Text component="ol" mt={0} mb="xs" style={{ paddingLeft: '1.5rem' }}>
                {children}
              </Text>
            ),
            ul: ({ children }) => (
              <Text component="ul" mt={0} mb="xs" style={{ paddingLeft: '1.5rem' }}>
                {children}
              </Text>
            ),
          }}>
          {data.cmQuery.data.presentationObjectives}
        </ReactMarkdown>
      ) : (
        'Not specified'
      ),
    },
    */
  ]

  return (
    <CollapsibleContentSection
      id="customize-delivery"
      title="Delivery"
      subtitle="How the Sidekick presents this module to members."
      ContentElement={<TableContentGrid headingFlexWidth="245px" rows={rows} />}
    />
  )
}
